import React, { useCallback, useEffect, useState } from "react";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { api } from "../../../../api/apiClient";
import { notify } from "../../../components-ui/toast";
import {
  ServiceInstanceAvailabilityDto,
  ServiceInstanceDto,
  ServiceInstanceRotaDto,
  ServiceInstanceRotaPoolDto,
} from "../../../types/types";
import { RotaExportData } from "./rotaDocumentService";

interface MultiServiceInstanceRotaBuilderProps {
  instances: ServiceInstanceDto[];
  onExportDataChange?: (rotas: RotaExportData[] | null) => void;
}

interface InstanceRotaData {
  pool: ServiceInstanceRotaPoolDto[];
  availability: ServiceInstanceAvailabilityDto[];
  rota: ServiceInstanceRotaDto[];
}

type BoardStatus = "available" | "requested" | "confirmed" | "unavailable";

interface BoardPerson {
  partyId: number;
  serviceEnrollmentRoleId: number;
  name: string;
  roleName: string;
  availabilityStatus?: string | null;
  assignments?: ServiceInstanceRotaDto[];
  qualificationStatus?: "Compliant" | "Warning" | "Missing";
  qualificationIssues?: string[];
}

interface StatusGroupDefinition {
  key: BoardStatus;
  label: string;
  icon: React.ReactNode;
}

const statusGroups: StatusGroupDefinition[] = [
  {
    key: "available",
    label: "Available",
    icon: <GroupOutlinedIcon fontSize="small" />,
  },
  {
    key: "requested",
    label: "Requested",
    icon: <MailOutlineIcon fontSize="small" />,
  },
  {
    key: "confirmed",
    label: "Confirmed",
    icon: <CheckCircleOutlineIcon fontSize="small" />,
  },
  {
    key: "unavailable",
    label: "Unavailable",
    icon: <CancelOutlinedIcon fontSize="small" />,
  },
];

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

const isActiveRota = (item: ServiceInstanceRotaDto) =>
  item.status !== "Cancelled" && item.status !== "Removed";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

export default function MultiServiceInstanceRotaBuilder({
  instances,
  onExportDataChange,
}: MultiServiceInstanceRotaBuilderProps) {
  const [dataByInstanceId, setDataByInstanceId] = useState<
    Record<number, InstanceRotaData>
  >({});
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [notificationChannel, setNotificationChannel] =
    useState<"Email" | "SMS">("Email");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onExportDataChange?.(null);
      const entries = await Promise.all(
        instances.map(async (instance) => {
          const [pool, availability, rota] = await Promise.all([
            api<ServiceInstanceRotaPoolDto[]>(
              `/api/service-instances/${instance.serviceInstanceId}/rota-pool`,
            ),
            api<ServiceInstanceAvailabilityDto[]>(
              `/api/service-instances/${instance.serviceInstanceId}/availability`,
            ),
            api<ServiceInstanceRotaDto[]>(
              `/api/service-instances/${instance.serviceInstanceId}/rota`,
            ),
          ]);

          return [
            instance.serviceInstanceId,
            {
              pool: pool ?? [],
              availability: availability ?? [],
              rota: rota ?? [],
            },
          ] as const;
        }),
      );

      const nextData = Object.fromEntries(entries);
      setDataByInstanceId(nextData);
      onExportDataChange?.(
        instances.map((instance) => ({
          instance,
          assignments: nextData[instance.serviceInstanceId]?.rota ?? [],
        })),
      );
    } catch (error) {
      console.error("Failed to load rota board", error);
      notify.error("Failed to load the selected rotas.");
    } finally {
      setLoading(false);
    }
  }, [instances, onExportDataChange]);

  useEffect(() => {
    setSelectedRequests([]);
    load();
  }, [load]);

  const getLatestAvailability = (
    data: InstanceRotaData,
    partyId: number,
  ) => {
    return data.availability
      .filter((item) => item.partyId === partyId)
      .sort(
        (left, right) =>
          new Date(right.createdDate).getTime() -
          new Date(left.createdDate).getTime(),
      )[0];
  };

  const joinRoleNames = (roleNames: string[]) =>
    [...new Set(roleNames.filter(Boolean))].sort().join(", ");

  const getGroups = (instanceId: number): Record<BoardStatus, BoardPerson[]> => {
    const data = dataByInstanceId[instanceId] ?? {
      pool: [],
      availability: [],
      rota: [],
    };
    const assignments = data.rota.filter(isActiveRota);
    const assignmentsByParty = assignments.reduce<
      Record<number, ServiceInstanceRotaDto[]>
    >((groups, assignment) => {
      groups[assignment.partyId] = groups[assignment.partyId] ?? [];
      groups[assignment.partyId].push(assignment);
      return groups;
    }, {});

    const poolByParty = data.pool.reduce<
      Record<number, ServiceInstanceRotaPoolDto[]>
    >((groups, person) => {
      groups[person.partyId] = groups[person.partyId] ?? [];
      groups[person.partyId].push(person);
      return groups;
    }, {});

    const groups: Record<BoardStatus, BoardPerson[]> = {
      available: [],
      requested: [],
      confirmed: Object.values(assignmentsByParty).map((partyAssignments) => ({
        partyId: partyAssignments[0].partyId,
        serviceEnrollmentRoleId:
          partyAssignments[0].serviceEnrollmentRoleId,
        name: partyAssignments[0].partyName,
        roleName: joinRoleNames(
          partyAssignments.map((assignment) => assignment.serviceRoleName),
        ),
        assignments: partyAssignments,
        qualificationStatus: poolByParty[partyAssignments[0].partyId]?.some((role) => role.qualificationStatus === "Missing") ? "Missing" : poolByParty[partyAssignments[0].partyId]?.some((role) => role.qualificationStatus === "Warning") ? "Warning" : "Compliant",
        qualificationIssues: poolByParty[partyAssignments[0].partyId]?.flatMap((role) => role.qualificationIssues ?? []) ?? [],
      })),
      unavailable: [],
    };

    Object.values(poolByParty).forEach((partyRoles) => {
      const person = partyRoles[0];
      if (assignmentsByParty[person.partyId]) return;

      const latestAvailability = getLatestAvailability(data, person.partyId);
      const representativeRole = latestAvailability
        ? partyRoles.find(
            (role) =>
              role.serviceEnrollmentRoleId ===
              latestAvailability.serviceEnrollmentRoleId,
          ) ?? person
        : person;
      const availabilityStatus =
        latestAvailability?.status ??
        partyRoles.find((role) => role.availabilityStatus)?.availabilityStatus ??
        null;
      const boardPerson: BoardPerson = {
        partyId: person.partyId,
        serviceEnrollmentRoleId: representativeRole.serviceEnrollmentRoleId,
        name: person.partyDisplayName,
        roleName: joinRoleNames(partyRoles.map((role) => role.roleName)),
        availabilityStatus,
        qualificationStatus: partyRoles.some((role) => role.qualificationStatus === "Missing") ? "Missing" : partyRoles.some((role) => role.qualificationStatus === "Warning") ? "Warning" : "Compliant",
        qualificationIssues: partyRoles.flatMap((role) => role.qualificationIssues ?? []),
      };

      if (availabilityStatus === "Requested") {
        groups.requested.push(boardPerson);
      } else if (availabilityStatus === "Unavailable") {
        groups.unavailable.push(boardPerson);
      } else {
        groups.available.push(boardPerson);
      }
    });

    Object.values(groups).forEach((people) =>
      people.sort((left, right) => left.name.localeCompare(right.name)),
    );
    return groups;
  };

  const assignmentKey = (instanceId: number, roleId: number) =>
    `${instanceId}:${roleId}`;

  const toggleRequest = (
    instanceId: number,
    person: BoardPerson,
  ) => {
    const key = assignmentKey(instanceId, person.serviceEnrollmentRoleId);
    setSelectedRequests((current) =>
      current.includes(key)
        ? current.filter((selectedKey) => selectedKey !== key)
        : [...current, key],
    );
  };

  const selectGroup = (instanceId: number, people: BoardPerson[]) => {
    const groupKeys = people.map((person) =>
      assignmentKey(instanceId, person.serviceEnrollmentRoleId),
    );
    setSelectedRequests((current) => Array.from(new Set([...current, ...groupKeys])));
  };

  const deselectGroup = (instanceId: number, people: BoardPerson[]) => {
    const groupKeys = new Set(
      people.map((person) =>
        assignmentKey(instanceId, person.serviceEnrollmentRoleId),
      ),
    );
    setSelectedRequests((current) =>
      current.filter((selectedKey) => !groupKeys.has(selectedKey)),
    );
  };

  const sendAvailabilityRequests = async () => {
    if (selectedRequests.length === 0) return;

    try {
      setSaving(true);
      const selections = selectedRequests.map((key) => {
        const [serviceInstanceId, serviceEnrollmentRoleId] = key
          .split(":")
          .map(Number);
        return { serviceInstanceId, serviceEnrollmentRoleId };
      });

      await api<void>("/api/service-instances/availability/request", {
        method: "POST",
        body: { selections, channel: notificationChannel },
      });
      notify.success("Availability requests sent.");
      setSelectedRequests([]);
      await load();
    } catch (error) {
      console.error("Failed to send availability requests", error);
      notify.error("Failed to send availability requests.");
    } finally {
      setSaving(false);
    }
  };

  const removeAssignments = async (person: BoardPerson) => {
    const assignments = person.assignments ?? [];
    if (assignments.length === 0) return;

    const key = `remove-party-${person.partyId}`;
    try {
      setSavingKey(key);
      await Promise.all(
        assignments.map((assignment) =>
          api<void>(
            `/api/service-instance-rota/${assignment.serviceInstanceRotaId}`,
            { method: "DELETE" },
          ),
        ),
      );
      notify.success(
        assignments.length === 1
          ? "Rota assignment cancelled."
          : "Rota assignments cancelled.",
      );
      await load();
    } catch (error) {
      console.error("Failed to cancel rota assignment", error);
      notify.error("Failed to cancel rota assignment.");
    } finally {
      setSavingKey(null);
    }
  };

  const updateAvailability = async (
    serviceInstanceId: number,
    person: BoardPerson,
    status: "Confirmed" | "Available" | "Unavailable",
  ) => {
    const key = `availability-${serviceInstanceId}-${person.partyId}`;
    try {
      setSavingKey(key);
      await api<void>(
        `/api/service-instances/${serviceInstanceId}/availability/status`,
        {
          method: "PUT",
          body: {
            serviceEnrollmentRoleId: person.serviceEnrollmentRoleId,
            status,
          },
        },
      );
      notify.success(
        status === "Confirmed"
          ? `${person.name} confirmed on the rota.`
          : status === "Unavailable"
            ? `${person.name} marked as unavailable.`
            : `${person.name} moved back to available.`,
      );
      await load();
    } catch (error) {
      console.error("Failed to update availability", error);
      notify.error("Failed to update availability.");
    } finally {
      setSavingKey(null);
    }
  };

  const toggleGroup = (instanceId: number, status: BoardStatus) => {
    const key = `${instanceId}-${status}`;
    setExpandedGroups((current) => ({
      ...current,
      [key]: !(current[key] ?? true),
    }));
  };

  const renderPerson = (
    instance: ServiceInstanceDto,
    status: BoardStatus,
    person: BoardPerson,
  ) => {
    const key = assignmentKey(
      instance.serviceInstanceId,
      person.serviceEnrollmentRoleId,
    );
    return (
      <div className="rota-board-person" key={person.partyId}>
        <input
          type="checkbox"
          checked={selectedRequests.includes(key)}
          disabled={saving}
          onChange={() => toggleRequest(instance.serviceInstanceId, person)}
          aria-label={`Select ${person.name}`}
          title="Select for an availability request"
        />
        <span className={`rota-person-avatar rota-person-avatar--${status}`}>
          {getInitials(person.name)}
        </span>
        <span className="rota-board-person-name" title={person.name}>{person.name}</span>
        <span className="rota-qualification-indicator">
          {person.qualificationStatus && person.qualificationStatus !== "Compliant" && (
            <WarningAmberIcon className={`rota-qualification-warning rota-qualification-warning--${person.qualificationStatus.toLowerCase()}`} fontSize="small" titleAccess={person.qualificationIssues?.join("; ")} />
          )}
        </span>

        {status === "available" || status === "requested" ? (
          <span className="rota-row-actions">
            <button
              type="button"
              className="rota-row-action rota-row-action--confirm"
              title="Confirm on rota"
              aria-label={`Confirm ${person.name} on this rota`}
              disabled={
                savingKey ===
                `availability-${instance.serviceInstanceId}-${person.partyId}`
              }
              onClick={() =>
                updateAvailability(
                  instance.serviceInstanceId,
                  person,
                  "Confirmed",
                )
              }
            >
              <CheckCircleOutlineIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="rota-row-action rota-row-action--remove"
              title="Mark unavailable"
              aria-label={`Mark ${person.name} as unavailable`}
              disabled={
                savingKey ===
                `availability-${instance.serviceInstanceId}-${person.partyId}`
              }
              onClick={() =>
                updateAvailability(
                  instance.serviceInstanceId,
                  person,
                  "Unavailable",
                )
              }
            >
              <CancelOutlinedIcon fontSize="small" />
            </button>
          </span>
        ) : person.assignments ? (
          <button
            type="button"
            className="rota-row-action rota-row-action--remove"
            title="Remove from rota"
            aria-label={`Remove ${person.name} from this rota`}
            disabled={savingKey === `remove-party-${person.partyId}`}
            onClick={() => removeAssignments(person)}
          >
            <CloseIcon fontSize="small" />
          </button>
        ) : status === "unavailable" ? (
          <button
            type="button"
            className="rota-row-action rota-row-action--remove"
            title="Move back to available"
            aria-label={`Move ${person.name} back to available`}
            disabled={
              savingKey ===
              `availability-${instance.serviceInstanceId}-${person.partyId}`
            }
            onClick={() =>
              updateAvailability(
                instance.serviceInstanceId,
                person,
                "Available",
              )
            }
          >
            <CloseIcon fontSize="small" />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className="rota-board-workspace">
      <div className="rota-board-toolbar">
        <div className="rota-request-instructions">
          Select people from any section. Each person will receive one
          {notificationChannel === "Email" ? " email" : " SMS message"} covering
          all selected sessions.
        </div>

        <div className="rota-request-actions">
          <select
            className="form-input rota-notification-channel"
            aria-label="Availability notification channel"
            value={notificationChannel}
            onChange={(event) =>
              setNotificationChannel(event.target.value as "Email" | "SMS")
            }
          >
            <option value="Email">Email</option>
            <option value="SMS">SMS</option>
          </select>

          <button
            type="button"
            className="action-button primary rota-save-button"
            onClick={sendAvailabilityRequests}
            disabled={selectedRequests.length === 0 || saving}
          >
            <SaveOutlinedIcon fontSize="small" />
            Request availability
            {selectedRequests.length > 0 ? ` (${selectedRequests.length})` : ""}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card rota-board-loading">Loading selected rotas...</div>
      ) : (
        <div className="rota-board-scroll">
          <div
            className="rota-session-board"
            style={{ gridTemplateColumns: `repeat(${instances.length}, minmax(280px, 1fr))` }}
          >
            {instances.map((instance) => {
              const start = new Date(instance.startDateTime);
              const end = new Date(instance.endDateTime);
              const groups = getGroups(instance.serviceInstanceId);

              return (
                <section className="rota-board-column" key={instance.serviceInstanceId}>
                  <header className="rota-board-column-header">
                    <h3>{dateFormatter.format(start)}</h3>
                    <span>
                      {timeFormatter.format(start)} - {timeFormatter.format(end)}
                    </span>
                    <div className="rota-board-counts">
                      {statusGroups.map((group) => (
                        <div key={group.key} className={`rota-board-count rota-board-count--${group.key}`}>
                          <strong>{groups[group.key].length}</strong>
                          <span>{group.label}</span>
                        </div>
                      ))}
                    </div>
                  </header>

                  <div className="rota-board-groups">
                    {statusGroups.map((group) => {
                        const groupKey = `${instance.serviceInstanceId}-${group.key}`;
                        const expanded = expandedGroups[groupKey] ?? true;
                        const visiblePeople = groups[group.key];
                        const visibleKeys = visiblePeople.map((person) =>
                          assignmentKey(
                            instance.serviceInstanceId,
                            person.serviceEnrollmentRoleId,
                          ),
                        );
                        const selectedVisibleCount = visibleKeys.filter((key) =>
                          selectedRequests.includes(key),
                        ).length;
                        const allVisibleSelected =
                          visibleKeys.length > 0 &&
                          selectedVisibleCount === visibleKeys.length;

                        return (
                          <section
                            key={group.key}
                            className={`rota-status-group rota-status-group--${group.key}`}
                          >
                            <div className="rota-status-group-header">
                              <button
                                type="button"
                                className="rota-status-group-toggle"
                                onClick={() =>
                                  toggleGroup(instance.serviceInstanceId, group.key)
                                }
                                aria-expanded={expanded}
                              >
                                <span className="rota-status-group-title">
                                  {group.icon}
                                  {group.label} ({visiblePeople.length})
                                </span>
                                {expanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </button>
                              <div className="rota-status-group-selection-actions">
                                <button
                                  type="button"
                                  onClick={() =>
                                    selectGroup(instance.serviceInstanceId, visiblePeople)
                                  }
                                  disabled={
                                    visiblePeople.length === 0 || allVisibleSelected || saving
                                  }
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    deselectGroup(instance.serviceInstanceId, visiblePeople)
                                  }
                                  disabled={selectedVisibleCount === 0 || saving}
                                >
                                  Deselect all
                                </button>
                              </div>
                            </div>

                            {expanded && (
                              <div className="rota-status-group-people">
                                {visiblePeople.length > 0 ? (
                                  visiblePeople.map((person) =>
                                    renderPerson(instance, group.key, person),
                                  )
                                ) : (
                                  <div className="rota-board-empty-group">No matching people</div>
                                )}
                              </div>
                            )}
                          </section>
                        );
                      })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
